import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { redditLeadsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Search,
  ExternalLink,
  User,
  MapPin,
  Clock,
  DollarSign,
  MessageSquare,
  Copy,
  ChevronDown,
  ChevronUp,
  Flame,
  Zap,
  Snowflake,
  X,
  Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const STATUS_OPTIONS = ['new', 'contacted', 'following-up', 'negotiating', 'converted', 'lost', 'ignored'];
const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700',
  contacted: 'bg-amber-50 text-amber-700',
  'following-up': 'bg-purple-50 text-purple-700',
  negotiating: 'bg-indigo-50 text-indigo-700',
  converted: 'bg-emerald-50 text-emerald-700',
  lost: 'bg-red-50 text-red-700',
  ignored: 'bg-zinc-100 text-zinc-500',
};
const CLASSIFICATION_ICONS = {
  hot: { icon: Flame, color: 'text-red-500', bg: 'bg-red-50' },
  warm: { icon: Zap, color: 'text-amber-500', bg: 'bg-amber-50' },
  cold: { icon: Snowflake, color: 'text-blue-400', bg: 'bg-blue-50' },
};

const ScoreBadge = ({ score }) => {
  if (!score && score !== 0) return null;
  const color = score >= 70 ? 'text-emerald-700 bg-emerald-50' : score >= 40 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50';
  return <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${color}`}>{score}</span>;
};

const RedditLeadGen = () => {
  const { activeBrand } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterClass !== 'all') params.classification = filterClass;
      const [leadsRes, statsRes] = await Promise.all([
        redditLeadsAPI.getAll(params),
        redditLeadsAPI.getStats(),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterClass]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const update = { status: newStatus };
      if (newStatus === 'contacted') update.contactedAt = new Date().toISOString();
      if (newStatus === 'converted') update.convertedAt = new Date().toISOString();
      await redditLeadsAPI.update(leadId, update);
      setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, ...update } : l));
      if (selected?.id === leadId) setSelected((s) => ({ ...s, ...update }));
      toast.success(`Status updated to ${newStatus}`);
      redditLeadsAPI.getStats().then((r) => setStats(r.data));
    } catch { toast.error('Failed to update'); }
  };

  const handleNotesChange = async (leadId, notes) => {
    try {
      await redditLeadsAPI.update(leadId, { notes });
      setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, notes } : l));
    } catch { /* silent */ }
  };

  const handleDelete = async (leadId) => {
    try {
      await redditLeadsAPI.delete(leadId);
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      setSelected(null);
      toast.success('Lead deleted');
      redditLeadsAPI.getStats().then((r) => setStats(r.data));
    } catch { toast.error('Failed to delete'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filtered = leads.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.username || '').toLowerCase().includes(q) ||
           (l.postTitle || '').toLowerCase().includes(q) ||
           (l.summary || '').toLowerCase().includes(q) ||
           (l.location || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-4" data-testid="reddit-leads-loading">
        <div className="h-8 w-64 bg-zinc-100 rounded" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-zinc-100 rounded" />)}</div>
        <div className="h-96 bg-zinc-100 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-5 fade-in" data-testid="reddit-leads-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-medium text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
          Reddit AI Lead Gen
        </h1>
        <p className="text-zinc-500 text-sm mt-1">AI-discovered leads from Reddit jewelry communities</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="leads-stats">
          <div className="bg-white border border-zinc-100 rounded-sm p-4">
            <p className="text-2xl font-semibold text-zinc-900">{stats.total}</p>
            <p className="text-xs text-zinc-400 uppercase tracking-widest mt-1">Total Leads</p>
          </div>
          <div className="bg-white border border-zinc-100 rounded-sm p-4">
            <p className="text-2xl font-semibold text-red-500">{stats.by_classification?.hot || 0}</p>
            <p className="text-xs text-zinc-400 uppercase tracking-widest mt-1">Hot Leads</p>
          </div>
          <div className="bg-white border border-zinc-100 rounded-sm p-4">
            <p className="text-2xl font-semibold text-emerald-600">{stats.by_status?.converted || 0}</p>
            <p className="text-xs text-zinc-400 uppercase tracking-widest mt-1">Converted</p>
          </div>
          <div className="bg-white border border-zinc-100 rounded-sm p-4">
            <p className="text-2xl font-semibold text-zinc-900">${stats.total_converted_value?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-400 uppercase tracking-widest mt-1">Revenue</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text" placeholder="Search leads..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-400"
            data-testid="leads-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-[150px]" data-testid="filter-classification"><SelectValue placeholder="Classification" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="hot">Hot</SelectItem>
            <SelectItem value="warm">Warm</SelectItem>
            <SelectItem value="cold">Cold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lead Cards */}
      {filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((lead) => {
            const cls = CLASSIFICATION_ICONS[lead.classification] || CLASSIFICATION_ICONS.cold;
            const ClsIcon = cls.icon;
            const isExpanded = expanded[lead.id];
            return (
              <div key={lead.id} className="bg-white border border-zinc-100 rounded-sm transition-all hover:border-zinc-200" data-testid={`lead-card-${lead.id}`}>
                {/* Main row */}
                <div className="flex items-center gap-4 px-4 py-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${cls.bg}`}>
                    <ClsIcon className={`w-4 h-4 ${cls.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-800 truncate">{lead.postTitle || lead.summary || 'Untitled'}</span>
                      <ScoreBadge score={lead.score} />
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-400">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> u/{lead.username}</span>
                      {lead.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.location}</span>}
                      {lead.budget && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {lead.budget}</span>}
                      {lead.timeline && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {lead.timeline}</span>}
                      {lead.source && <span>r/{lead.source.replace('r/', '')}</span>}
                    </div>
                  </div>
                  <Select value={lead.status || 'new'} onValueChange={(v) => handleStatusChange(lead.id, v)}>
                    <SelectTrigger className={`w-[130px] text-xs h-8 ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`} data-testid={`status-${lead.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => setExpanded((p) => ({ ...p, [lead.id]: !p[lead.id] }))} className="text-zinc-400 hover:text-zinc-600" data-testid={`expand-${lead.id}`}>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-zinc-100 px-4 py-3 space-y-3 bg-zinc-50/40">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      {lead.stylePreferences && <div><p className="text-[10px] uppercase text-zinc-400 tracking-widest">Style</p><p className="text-zinc-700">{lead.stylePreferences}</p></div>}
                      {lead.metalPreference && <div><p className="text-[10px] uppercase text-zinc-400 tracking-widest">Metal</p><p className="text-zinc-700">{lead.metalPreference}</p></div>}
                      {lead.caratRange && <div><p className="text-[10px] uppercase text-zinc-400 tracking-widest">Carat</p><p className="text-zinc-700">{lead.caratRange}</p></div>}
                      {lead.intent && <div><p className="text-[10px] uppercase text-zinc-400 tracking-widest">Intent</p><p className="text-zinc-700">{lead.intent}</p></div>}
                    </div>
                    {lead.painPoints && (
                      <div><p className="text-[10px] uppercase text-zinc-400 tracking-widest mb-1">Pain Points</p><p className="text-sm text-zinc-700">{lead.painPoints}</p></div>
                    )}
                    {lead.suggestedResponse && (
                      <div className="bg-white border border-zinc-200 rounded-sm p-3">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] uppercase text-zinc-400 tracking-widest flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Suggested Response</p>
                          <button onClick={() => copyToClipboard(lead.suggestedResponse)} className="text-xs text-zinc-400 hover:text-zinc-600 flex items-center gap-1" data-testid={`copy-response-${lead.id}`}>
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>
                        <p className="text-sm text-zinc-700 whitespace-pre-wrap">{lead.suggestedResponse}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      {lead.postUrl && (
                        <a href={lead.postUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> View Post
                        </a>
                      )}
                      {lead.profileUrl && (
                        <a href={lead.profileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          <User className="w-3 h-3" /> Profile
                        </a>
                      )}
                      <button onClick={() => handleDelete(lead.id)} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 ml-auto" data-testid={`delete-${lead.id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                    {/* Notes */}
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400 tracking-widest mb-1">Notes</p>
                      <textarea
                        defaultValue={lead.notes || ''}
                        onBlur={(e) => handleNotesChange(lead.id, e.target.value)}
                        placeholder="Add notes..."
                        className="w-full text-sm border border-zinc-200 rounded-sm p-2 focus:outline-none focus:border-zinc-400 resize-none"
                        rows={2}
                        data-testid={`notes-${lead.id}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20 bg-white border border-zinc-100 rounded-sm">
          <Search className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            No leads yet
          </h3>
          <p className="text-zinc-500 text-sm">Your AI lead generator will populate leads here automatically</p>
        </div>
      )}
    </div>
  );
};

export default RedditLeadGen;
